import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

const DISMISSED_KEY = 'ff_pwa_install_dismissed_at';
// Re-surface the prompt after this long if the user dismissed it instead of installing.
const REPROMPT_AFTER_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Subtle "Install app" prompt for the dashboard.
 *
 * Browser support:
 *   - Chrome/Edge/Brave/Opera fire `beforeinstallprompt` when criteria are met
 *     (manifest + SW + https + not already installed). We capture the event,
 *     hide the default mini-infobar, and surface our own banner.
 *   - iOS Safari does NOT fire this event. We show a tiny "Add to Home Screen"
 *     hint instead, but only on real iOS Safari (not Chrome on iOS which is
 *     also Safari under the hood — keeping this simple to avoid false hits).
 *
 * Lifecycle:
 *   - User clicks "Install" → call `prompt()` → either installed (event fires
 *     → we hide forever) or dismissed → we re-prompt after 30 days.
 *   - User clicks the close (X) → same 30-day cooldown.
 */
const InstallPwaBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Already installed (running in standalone mode)? Never show.
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Cooldown check
    let dismissedAt = 0;
    try { dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10); } catch {}
    if (dismissedAt && Date.now() - dismissedAt < REPROMPT_AFTER_MS) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { localStorage.setItem(DISMISSED_KEY, String(Date.now() + 100 * 365 * 24 * 60 * 60 * 1000)); } catch {}
    };
    window.addEventListener('appinstalled', handleInstalled);

    // iOS Safari fallback (event never fires there)
    const ua = window.navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIos && isSafari) setShowIosHint(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice.catch(() => ({ outcome: 'dismissed' }));
    if (outcome === 'dismissed') {
      try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch {}
    }
    setVisible(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch {}
    setVisible(false);
    setShowIosHint(false);
  };

  if (!visible && !showIosHint) return null;

  // iOS-specific hint takes priority when present and `beforeinstallprompt` isn't available.
  if (!visible && showIosHint) {
    return (
      <div
        className="mb-6 flex items-start gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm"
        data-testid="install-pwa-banner-ios"
      >
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: '#FE6A6B15', color: '#FE6A6B' }}
        >
          <Download className="h-4 w-4" />
        </div>
        <div className="flex-1 text-sm">
          <p className="font-medium text-zinc-900">Install Fantastic Forms on your iPhone</p>
          <p className="mt-0.5 text-xs text-zinc-500">
            Tap the <span className="font-semibold">Share</span> button in Safari, then
            <span className="font-semibold"> Add to Home Screen</span> for an app-like experience.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-zinc-400 hover:text-zinc-700"
          aria-label="Dismiss install hint"
          data-testid="install-pwa-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="mb-6 flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm"
      data-testid="install-pwa-banner"
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: '#FE6A6B15', color: '#FE6A6B' }}
      >
        <Download className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-zinc-900">Install Fantastic Forms</p>
        <p className="text-xs text-zinc-500">
          Add it to your dock for one-click access — works offline, no browser tabs needed.
        </p>
      </div>
      <Button
        size="sm"
        onClick={handleInstallClick}
        className="h-8"
        data-testid="install-pwa-banner-install"
      >
        Install
      </Button>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-zinc-400 hover:text-zinc-700"
        aria-label="Dismiss install prompt"
        data-testid="install-pwa-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default InstallPwaBanner;
